import { useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { LuCalendarDays, LuClock, LuHash, LuMapPin } from 'react-icons/lu';

import { ApiError } from '@mds/shared/lib/api';
import { clockRange } from '@mds/shared/lib/format';
import type { GuestBookingFeedbackForm } from '@mds/shared/types/booking-feedback';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { RatingScale } from '@mds/shared/ui/form/rating-scale';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Textarea } from '@mds/shared/ui/shadcn/textarea';

import FormQuestions from '@/components/form/form-questions';
import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { submitBookingFeedback } from '@/features/feedback/booking-feedback.api';
import { feedbackQueries } from '@/features/feedback/feedback.queries';
import { feedbackDefaults, feedbackSchema } from '@/features/feedback/feedback.schema';
import { formatEventDate } from '@/lib/format';

const band = 'mx-auto max-w-page px-gutter pt-section';
/**
 * The form's column, centred on the page with its header. `box-content` keeps
 * the gutter outside the cap, so the column is the same width at every size.
 */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

type Booking = GuestBookingFeedbackForm['booking'];

/** "Thu 17/09/26", or the first and last day of a package. */
const datesOf = (booking: Booking) =>
  booking.bookingEndDate === booking.bookingDate
    ? formatEventDate(booking.bookingDate)
    : `${formatEventDate(booking.bookingDate)} – ${formatEventDate(booking.bookingEndDate)}`;

/** A room's hours; a full-day desk has none. */
const timeOf = (booking: Booking) =>
  booking.startMinute !== null && booking.endMinute !== null
    ? clockRange(booking.startMinute, booking.endMinute)
    : null;

/** The rows of the booking card on the closing screens. */
const bookingDetails = (booking: Booking) => {
  const time = timeOf(booking);
  return {
    title: booking.spaceName,
    items: [
      { icon: LuMapPin, marker: 'Booked', description: booking.itemName },
      { icon: LuCalendarDays, marker: 'Date', description: datesOf(booking) },
      ...(time ? [{ icon: LuClock, marker: 'Time', description: time }] : []),
      { icon: LuHash, marker: 'Reference', description: booking.reference },
    ],
  };
};

const toSpaces = (
  <Button asChild variant="outline" size="lg">
    <Link to="/spaces">See our spaces</Link>
  </Button>
);

/**
 * The page behind the link in the booking feedback email.
 *
 * The token in the URL is the client's only key — there is no sign-in — so the
 * page has five ends: a link that opens nothing, a link left too long, a form
 * already sent, the form itself, and the thanks once it is sent.
 */
export default function BookingFeedbackPage() {
  const { token = '' } = useParams();
  const resolved = useQuery(feedbackQueries.bookingForm(token));
  /** Set once this visit has sent the form, or the server says an earlier one did. */
  const [sent, setSent] = useState<'now' | 'before' | null>(null);

  if (resolved.isError && resolved.error instanceof ApiError) {
    if (resolved.error.status === 404) {
      return (
        <div className={band}>
          <OutcomePanel
            tone="neutral"
            title="This feedback link doesn't open a form"
            description="It may be cut short or mistyped. Open it again from your email, or copy the whole link into your browser."
            actions={toSpaces}
          />
        </div>
      );
    }
    if (resolved.error.code === 'BOOKING_FEEDBACK_EXPIRED') {
      return (
        <div className={band}>
          <OutcomePanel
            tone="neutral"
            title="This feedback link has expired"
            description="Feedback links work for 30 days. If you'd still like to tell us something, we'd be glad to hear it."
            actions={
              <>
                <Button asChild size="lg">
                  <Link to="/contact">Contact us</Link>
                </Button>
                {toSpaces}
              </>
            }
          />
        </div>
      );
    }
  }

  if (resolved.isPending || resolved.isError) {
    return (
      <section className={band}>
        {resolved.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock title="We couldn't load your feedback form" error={resolved.error} />
        )}
      </section>
    );
  }

  const form = resolved.data;

  if (sent || form.alreadySubmitted) {
    const now = sent === 'now';
    return (
      <div className={band}>
        <OutcomePanel
          tone="success"
          title={now ? 'Thank you for your feedback' : 'You already sent your feedback'}
          description={
            now
              ? 'It goes straight to the MDS team, and it shapes what we improve next.'
              : `We have your answers about ${form.booking.spaceName}. Each link takes one response.`
          }
          details={bookingDetails(form.booking)}
          actions={toSpaces}
        />
      </div>
    );
  }

  const time = timeOf(form.booking);
  return (
    <section className={column}>
      <PageHeader
        title={`How was ${form.booking.spaceName}?`}
        description="Your answers go to the MDS team with your booking, so we can follow up if something wasn't right."
        meta={[form.booking.itemName, datesOf(form.booking), ...(time ? [time] : [])]}
      />
      <FeedbackForm
        token={token}
        form={form}
        onSent={() => setSent('now')}
        onAlreadySent={() => setSent('before')}
      />
    </section>
  );
}

function FeedbackForm({
  token,
  form,
  onSent,
  onAlreadySent,
}: {
  token: string;
  form: GuestBookingFeedbackForm;
  onSent: () => void;
  onAlreadySent: () => void;
}) {
  // The same body as event feedback: a rating, a comment, and answers by question.
  const schema = useMemo(() => feedbackSchema(form), [form]);
  const fields = useZodForm(schema, { defaultValues: feedbackDefaults(form) });
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);

  const sending = useMutation({
    mutationFn: (body: Parameters<typeof submitBookingFeedback>[1]) =>
      submitBookingFeedback(token, body),
    onMutate: () => setFormError(null),
    onSuccess: onSent,
    onError: (error) => {
      // Sent from another tab, or a retry after a lost response: the answers
      // are in, which is what the client wanted.
      if (error instanceof ApiError && error.code === 'BOOKING_FEEDBACK_ALREADY_SUBMITTED') {
        return onAlreadySent();
      }
      if (applyServerErrors(error, fields.setError)) return;
      // The per-question refusals name the question in their message, and an
      // expired link says so, so they read fine on their own.
      setFormError(error);
    },
  });

  const label = `Overall, how would you rate ${form.booking.spaceName}?`;
  return (
    <Form
      form={fields}
      className="mt-block flex flex-col gap-card"
      onSubmit={(body) => sending.mutate(body)}
    >
      {/* The rating opens the first card, with no title: the page heading
          already asks how it was. The form's own questions follow, the
          ungrouped ones in the same card and each section in its own. */}
      <FormQuestions form={form}>
        <Field name="overallRating" label={label} hint="1 is poor, 5 is excellent." required>
          <RatingScale aria-label={label} />
        </Field>

        <Field name="comment" label="Anything you'd like to tell us?">
          <Textarea rows={4} maxLength={5000} />
        </Field>
      </FormQuestions>

      {formError !== null && (
        <ErrorBlock
          title="We couldn't send your feedback"
          error={formError}
          onDismiss={() => setFormError(null)}
        />
      )}

      <div className="flex justify-center">
        <Button type="submit" size="lg" className="min-w-56" disabled={sending.isPending}>
          {sending.isPending ? 'Sending…' : 'Send feedback'}
        </Button>
      </div>
    </Form>
  );
}
